<template>
    <main class="departments-page argus-glass-page" aria-labelledby="departments-title">
        <header class="departments-heading">
            <div>
                <h1 id="departments-title">Отделы</h1>
                <p class="text-muted mb-0">Участники и рабочие разделы</p>
            </div>
            <b-button v-if="$can('create', 'users')" v-b-modal.createDepartment variant="success">
                <i class="fas fa-plus mr-1" aria-hidden="true"></i> Создать отдел
            </b-button>
        </header>
        <div class="departments-controls argus-glass-surface">
            <b-form-group label="Поиск отдела или участника" label-for="department-search" class="mb-0">
                <b-form-input id="department-search" v-model="filter" type="search" placeholder="Название, описание или имя" />
            </b-form-group>
            <b-form-group label="На странице" label-for="department-page-size" class="mb-0">
                <b-form-select id="department-page-size" v-model="perPage" :options="pageOptions" />
            </b-form-group>
            <div class="departments-mobile-sort">
                <b-form-group label="Сортировка" label-for="department-sort" class="mb-0">
                    <b-form-select id="department-sort" v-model="sortBy" :options="sortOptions" />
                </b-form-group>
                <b-button variant="outline-secondary" :aria-pressed="String(sortDesc)" @click="sortDesc = !sortDesc">
                    {{ sortDesc ? 'По убыванию' : 'По возрастанию' }}
                </b-button>
            </div>
        </div>
        <div v-if="loadError" class="departments-load-error" role="alert">
            <span>{{ loadError }}</span>
            <b-button variant="outline-danger" :disabled="loading" @click="loadDepartments">Повторить</b-button>
        </div>
        <b-table ref="departmentsTable" class="departments-table" stacked="md" :fields="fields" :filter="filter"
                 :items="items" :current-page="currentPage" :per-page="perPage" primary-key="id"
                 :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" :busy="loading"
                 show-empty empty-text="Отделов пока нет" empty-filtered-text="Ничего не найдено. Измените поиск."
                 @filtered="onFiltered">
            <template #table-busy><div class="p-3 text-center" role="status">Загружаем отделы…</div></template>
            <template #cell(name)="data"><strong class="department-name">{{ data.value }}</strong></template>
            <template #cell(description)="data">
                <blockquote v-if="data.value" class="user-quote department-description">{{ data.value }}</blockquote>
                <span v-else class="text-muted">Нет описания</span>
            </template>
            <template #cell(is_active)="data">
                <b-badge :variant="data.value ? 'success' : 'secondary'">{{ data.value ? 'Активен' : 'Неактивен' }}</b-badge>
            </template>
            <template #cell(default_workspace_key)="data">
                <span v-if="data.value">{{ workspaceLabel(data.value) }}</span>
                <span v-else class="text-muted">Не задан</span>
            </template>
            <template #cell(users)="data">
                <div v-if="departmentUsers(data.item).length" class="department-members">
                    <EmployeeLink v-for="user in departmentUsers(data.item).slice(0, 3)" :key="user.user_id" :user="user" fallback="—" />
                    <details v-if="departmentUsers(data.item).length > 3" :key="`members-${data.item.id}`" class="department-members-more">
                        <summary>Ещё участников: {{ departmentUsers(data.item).length - 3 }}</summary>
                        <div class="department-members">
                            <EmployeeLink v-for="user in departmentUsers(data.item).slice(3)" :key="user.user_id" :user="user" fallback="—" />
                        </div>
                    </details>
                </div>
                <span v-else class="text-muted">Нет участников</span>
            </template>
            <template #cell(actions)="data">
                <div class="department-actions">
                    <b-button v-if="$can('update', 'users')" variant="outline-primary" :aria-label="`Изменить отдел ${data.item.name}`" @click="onEdit(data.item)">
                        <i class="fas fa-pen mr-1" aria-hidden="true"></i> Изменить
                    </b-button>
                    <b-button v-if="$can('delete', 'users')" variant="outline-danger" :aria-label="`Удалить отдел ${data.item.name}`" @click="onDelete(data.item)">
                        <i class="fas fa-trash-alt mr-1" aria-hidden="true"></i> Удалить
                    </b-button>
                </div>
            </template>
        </b-table>
        <footer class="departments-pagination">
            <span class="text-muted" role="status">Найдено отделов: {{ totalRows }}</span>
            <b-pagination v-if="totalRows > perPage" v-model="currentPage" :total-rows="totalRows" :per-page="perPage"
                          :limit="3" hide-goto-end-buttons aria-label="Страницы отделов" class="mb-0"
                          label-page="Перейти на страницу" label-prev-page="Предыдущая страница" label-next-page="Следующая страница"
                          @page-click="onPageClick" />
        </footer>
        <create-department @changed="loadDepartments" />
        <delete-department @changed="loadDepartments" />
        <edit-department @changed="loadDepartments" />
    </main>
</template>

<script>
import EmployeeLink from '@/components/EmployeeLink.vue';
import {WORKSPACE_REGISTRY} from '@/utils/workspaceRegistry';
import CreateDepartment from './CreateDepartment/CreateDepartment.vue';
import DeleteDepartment from './DeleteDepartment/DeleteDepartment.vue';
import EditDepartment from './EditDepartment/EditDepartment.vue';
import {departmentErrorMessage} from './departmentDialog';
import './departments.css';

export default {
    name: 'Departments',
    components: {EmployeeLink, CreateDepartment, DeleteDepartment, EditDepartment},
    data() {
        return {
            filter: '', perPage: 20, pageOptions: [5, 10, 20, 50, 100, 200],
            totalRows: 0, currentPage: 1, sortBy: 'name', sortDesc: false,
            loading: false, loadError: ''
        };
    },
    computed: {
        items() { return this.$store.state.departments || []; },
        fields() {
            const fields = [
                {key: 'name', label: 'Название', sortable: true},
                {key: 'description', label: 'Описание', sortable: true},
                {key: 'is_active', label: 'Статус', sortable: true},
                {key: 'default_workspace_key', label: 'Главный раздел', sortable: true},
                {key: 'users', label: 'Участники'}
            ].map(field => ({...field, class: `departments-${field.key}-col`}));
            if (this.$can('update', 'users') || this.$can('delete', 'users')) {
                fields.push({key: 'actions', label: 'Действия', class: 'departments-actions-col'});
            }
            return fields;
        },
        sortOptions() { return this.fields.filter(field => field.sortable).map(field => ({value: field.key, text: field.label})); }
    },
    watch: {
        perPage() { this.currentPage = 1; },
        items() { if (!this.filter) this.totalRows = this.items.length; }
    },
    mounted() { this.loadDepartments(); },
    methods: {
        async loadDepartments() {
            this.loading = true;
            this.loadError = '';
            try {
                await this.$store.dispatch('getDepartments', {all: true, ignoreModal: true});
            } catch (err) {
                this.loadError = departmentErrorMessage(err, 'Не удалось загрузить отделы.');
            } finally {
                this.loading = false;
            }
        },
        onDelete({id}) {
            this.$store.commit('setSelectedDepartmentId', id);
            this.$bvModal.show('deleteDepartment');
        },
        onEdit({id}) {
            this.$store.commit('setSelectedDepartmentId', id);
            this.$bvModal.show('editDepartment');
        },
        onFiltered(filteredItems) {
            this.totalRows = filteredItems.length;
            this.currentPage = 1;
        },
        onPageClick(event, page) {
            if (page === this.currentPage || !window.matchMedia('(max-width: 767.98px)').matches) return;
            this.$nextTick(() => {
                const table = this.$refs.departmentsTable;
                if (table && table.$el) table.$el.scrollIntoView({block: 'start'});
            });
        },
        departmentUsers(department) { return Array.isArray(department && department.users) ? department.users : []; },
        workspaceLabel(key) {
            const workspace = WORKSPACE_REGISTRY.find(item => item.key === key);
            return workspace ? workspace.label : key;
        }
    }
};
</script>
